import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Check, Crown, Sparkles, Star, ArrowRight } from "lucide-react";

const TIER_ICONS = { essential: Sparkles, signature: Star, elite: Crown };

export default function Membership() {
  const [tiers, setTiers] = useState([]);
  const [mine, setMine] = useState(null);
  const [loadingTier, setLoadingTier] = useState("");
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/memberships").then((r) => setTiers(r.data));
    if (user) api.get("/memberships/me").then((r) => setMine(r.data)).catch(() => {});
  }, [user]);

  const subscribe = async (tier) => {
    if (!user) {
      toast.error("Please sign in or create an account first");
      nav("/register");
      return;
    }
    setLoadingTier(tier);
    try {
      const { data } = await api.post("/memberships/checkout", {
        tier,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoadingTier("");
    }
  };

  return (
    <div data-testid="membership-page" className="bg-[#F5EFE4]">
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-[#1A1815] overflow-hidden">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1522255272218-7ac5249be344?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1815] via-[#1A1815]/85 to-[#1A1815]/50" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <p className="overline">LSC Memberships</p>
          <h1 className="font-serif text-5xl lg:text-7xl mt-4 leading-[1.02] text-[#F5EFE4]">
            The Luxor life, <em className="italic text-[#C5A059]">monthly.</em>
          </h1>
          <div className="mt-6 flex justify-center"><span className="divider-gold-center" /></div>
          <p className="mt-6 max-w-xl mx-auto text-[#F5EFE4]/70">
            Three tiers, one seamless standard. On activation you receive a personal LSC code — use it every time you book.
          </p>
        </div>
      </section>

      {/* Active membership panel */}
      {mine?.active && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 -mt-8 relative z-20">
          <div className="bg-white border border-[#C5A059] p-6 md:p-8 flex flex-col md:flex-row gap-6 md:items-center md:justify-between shadow-[0_10px_40px_-20px_rgba(197,160,89,0.5)]" data-testid="active-membership-panel">
            <div>
              <p className="overline">Your Membership</p>
              <h3 className="font-serif text-3xl text-[#1A1815] mt-1">
                {mine.details?.name} · <span className="text-[#C5A059]">{mine.discount_percent}% off</span>
              </h3>
              {mine.cancel_at_period_end ? (
                <p className="text-xs text-red-700 mt-1">Cancels at end of billing period</p>
              ) : (
                <p className="text-xs text-[#6B665E] mt-1">Auto-renews monthly · Cancel anytime</p>
              )}
            </div>
            <div className="text-center md:text-right">
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#6B665E]">Personal Code</p>
              <p className="font-serif text-2xl text-[#C5A059] mt-1" data-testid="active-membership-code">{mine.membership_code}</p>
              <p className="text-xs text-[#6B665E] mt-1">Auto-applied when you book while signed in</p>
            </div>
          </div>
        </section>
      )}

      {/* Tiers */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-3 gap-6">
        {tiers.map((t) => {
          const Icon = TIER_ICONS[t.tier] || Sparkles;
          const isMine = mine?.tier === t.tier;
          const featured = t.featured;
          return (
            <div
              key={t.tier}
              data-testid={`tier-${t.tier}`}
              className={`relative bg-white border p-8 flex flex-col transition-all duration-300 ${featured ? "border-[#C5A059] shadow-[0_20px_60px_-30px_rgba(197,160,89,0.5)] md:-translate-y-4" : "border-[#E4D9C1] hover:border-[#C5A059]"}`}
            >
              {featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C5A059] text-white text-[10px] uppercase tracking-[0.24em] px-3 py-1">
                  Most Chosen
                </div>
              )}
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${featured ? "bg-[#C5A059] text-white" : "bg-[#F5EFE4] text-[#C5A059] border border-[#C5A059]/30"}`}>
                <Icon size={20} strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-4xl mt-6 text-[#1A1815]">{t.name}</h3>
              <p className="text-sm text-[#6B665E] mt-1 italic">{t.tagline}</p>

              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-serif text-5xl text-[#C5A059]">£{t.price}</span>
                <span className="text-sm text-[#6B665E]">/ month</span>
              </div>

              <ul className="mt-8 space-y-3 flex-1">
                {t.benefits.map((b) => (
                  <li key={b} className="flex gap-3 text-sm text-[#1A1815]">
                    <Check size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-3 flex items-center gap-2 text-xs text-[#6B665E]">
                <Check size={12} className="text-[#C5A059]" /> Auto-renews · Cancel anytime
              </div>

              <button
                type="button"
                onClick={() => subscribe(t.tier)}
                disabled={loadingTier === t.tier || isMine}
                data-testid={`tier-cta-${t.tier}`}
                className={`mt-8 w-full justify-center disabled:opacity-60 ${featured ? "luxor-btn-primary" : "luxor-btn-outline-dark"}`}
              >
                {isMine ? "Current Plan" : loadingTier === t.tier ? "Redirecting…" : (<><ArrowRight size={13} /> Join {t.name}</>)}
              </button>
            </div>
          );
        })}
      </section>

      {/* How it works */}
      <section className="bg-white border-y border-black/10 py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <p className="overline">How it works</p>
            <h2 className="font-serif text-4xl mt-4 text-[#1A1815]">Three steps to Luxor life.</h2>
            <div className="mt-4 flex justify-center"><span className="divider-gold-center" /></div>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              ["01", "Choose your tier", "Pick the level that fits your rhythm — Essential, Signature or Elite."],
              ["02", "Receive your code", "On activation we issue a personal LSC code, unique to you."],
              ["03", "Book with benefits", "Sign in and book — your discount and priority auto-apply every time."],
            ].map(([n, t, b]) => (
              <div key={n} className="border-l border-[#C5A059] pl-6">
                <p className="font-serif text-4xl text-[#C5A059]">{n}</p>
                <h4 className="font-serif text-2xl mt-2 text-[#1A1815]">{t}</h4>
                <p className="text-sm text-[#6B665E] mt-2 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 text-center">
        <div className="max-w-[900px] mx-auto px-6">
          <h2 className="font-serif text-3xl lg:text-4xl text-[#1A1815]">
            Not sure which tier suits you?
          </h2>
          <p className="mt-4 text-[#6B665E]">
            Our concierge team can walk you through — bespoke and confidential.
          </p>
          <div className="mt-6 flex justify-center gap-4 flex-wrap">
            <Link to="/contact" className="luxor-btn-outline-dark">Talk to concierge</Link>
            {!user && <Link to="/register" className="luxor-btn-primary" data-testid="membership-register">Create account</Link>}
          </div>
        </div>
      </section>
    </div>
  );
}
